import type { QuickFillItem } from '../../../shared/types';
import { resolveColumnKey } from '../../../shared/lib/table/columnMapping';
import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';

export function normalizeQuickFillLabel(value: string): string {
  return value.trim().toUpperCase();
}

function normalizeQuickFillAlias(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function getEndTypeFocusColumnAliases(label: string): Set<string> {
  const normalized = normalizeQuickFillAlias(label);
  if (normalized === 'END1' || normalized === 'ENDTYPE1' || normalized === 'PIXPPGEND1TYPE') {
    return new Set(['END1', 'ENDTYPE1', 'PIXPPGEND1TYPE']);
  }

  if (normalized === 'END2' || normalized === 'ENDTYPE2' || normalized === 'PIXPPGEND2TYPE') {
    return new Set(['END2', 'ENDTYPE2', 'PIXPPGEND2TYPE']);
  }

  return new Set([normalized]);
}

function getEndTypeSlotLabelCandidates(label: string): string[] {
  const normalized = normalizeQuickFillAlias(label);
  if (normalized === 'END1' || normalized === 'ENDTYPE1' || normalized === 'PIXPPGEND1TYPE') {
    return ['PiX_PPG_END1_TYPE', 'End1', 'END-TYPE_1', 'END-TYPE-1', label];
  }

  if (normalized === 'END2' || normalized === 'ENDTYPE2' || normalized === 'PIXPPGEND2TYPE') {
    return ['PiX_PPG_END2_TYPE', 'End2', 'END-TYPE_2', 'END-TYPE-2', label];
  }

  return [label, resolveColumnKey(label)];
}

function uniqueQuickFillLabels(labels: string[]): string[] {
  return Array.from(new Set(labels.map((label) => label.trim()).filter(Boolean)));
}

function resolveValidationQuickFillColumnLabel(
  label: string,
  properties: Array<{ label?: string; revitParameter?: string }>
): string {
  const normalizedLabel = normalizeQuickFillAlias(label);
  const directMatch = properties.find(
    (property) => normalizeQuickFillAlias(property.label || '') === normalizedLabel
  );
  if (directMatch?.label) {
    return directMatch.label;
  }

  const aliases = getEndTypeFocusColumnAliases(label);
  const parameterMatch = properties.find((property) =>
    aliases.has(normalizeQuickFillAlias(property.revitParameter || '')) ||
    aliases.has(normalizeQuickFillAlias(property.label || ''))
  );

  return parameterMatch?.label || label;
}

export function remapValidationQuickFillFocusColumnLabels(
  focus: ValidationQuickFillFocus | null,
  properties: Array<{ label?: string; revitParameter?: string }>
): ValidationQuickFillFocus | null {
  if (!focus) {
    return null;
  }

  const resolveLabel = (label: string) => resolveValidationQuickFillColumnLabel(label, properties);
  const labels = uniqueQuickFillLabels(focus.labels.map(resolveLabel));
  const autofillLabels = uniqueQuickFillLabels(focus.autofillLabels.map(resolveLabel));
  const suggestedCellValues = focus.suggestedCellValues?.map((suggestedCellValue) => ({
    ...suggestedCellValue,
    label: resolveLabel(suggestedCellValue.label),
  }));

  return {
    ...focus,
    labels,
    autofillLabels,
    primaryLabel: focus.primaryLabel ? resolveLabel(focus.primaryLabel) : focus.primaryLabel,
    ...(suggestedCellValues ? { suggestedCellValues } : {}),
  };
}

export function resolveQuickFillItemLabel(
  item: QuickFillItem | undefined,
  label: string
): string {
  if (!item) {
    return resolveColumnKey(label);
  }

  const readonlyFlags =
    (((item as any).__readonlyFlags as Record<string, boolean> | undefined) || {});
  const hasParamFlags =
    (((item as any).__hasParamFlags as Record<string, boolean> | undefined) || {});
  const bindingScopeFlags =
    (((item as any).__bindingScopeFlags as Record<string, string> | undefined) || {});
  const candidateLabels = Array.from(
    new Set(getEndTypeSlotLabelCandidates(label).map(resolveColumnKey).filter(Boolean))
  );
  const metadataLabels = [
    ...Object.keys(readonlyFlags),
    ...Object.keys(hasParamFlags),
    ...Object.keys(bindingScopeFlags),
  ];
  const rowLabels = Object.keys(item as Record<string, unknown>).filter((key) => !key.startsWith('__'));
  const availableLabels = Array.from(new Set([...metadataLabels, ...rowLabels]));

  for (const candidate of candidateLabels) {
    if (
      Object.prototype.hasOwnProperty.call(item, candidate) ||
      Object.prototype.hasOwnProperty.call(hasParamFlags, candidate) ||
      Object.prototype.hasOwnProperty.call(readonlyFlags, candidate) ||
      Object.prototype.hasOwnProperty.call(bindingScopeFlags, candidate)
    ) {
      return candidate;
    }
  }

  const normalizedCandidates = new Set(candidateLabels.map(normalizeQuickFillAlias));
  const aliasMatch = availableLabels.find((availableLabel) =>
    normalizedCandidates.has(normalizeQuickFillAlias(availableLabel))
  );

  return aliasMatch ?? resolveColumnKey(label);
}
